import api from './api';
import type { Solution } from './subProjectService';

export interface CreateSolutionRequest {
  name?: string;
  solution_description?: string;
  invention_summary?: string;
  claims?: string;
  fto_analysis?: string;
  industrialization?: string;
  patentability?: string;
  patent_application?: string;
}

export interface UpdateSolutionRequest {
  name?: string;
  solution_description?: string;
  invention_summary?: string;
  claims?: string;
  fto_analysis?: string;
  industrialization?: string;
  patentability?: string;
  patent_application?: string;
  context_kbs?: Record<string, string | null>;
}

export interface CreateSolutionResponse {
  solution: Solution;
  warn_many: boolean;
}

export const solutionService = {
  async list(subProjectId: string): Promise<Solution[]> {
    const response = await api.get<Solution[]>(`/sub-projects/${subProjectId}/solutions/`);
    return response.data;
  },

  async get(subProjectId: string, solutionId: string): Promise<Solution> {
    const response = await api.get<Solution>(`/sub-projects/${subProjectId}/solutions/${solutionId}`);
    return response.data;
  },

  async create(subProjectId: string, data: CreateSolutionRequest = {}): Promise<CreateSolutionResponse> {
    const response = await api.post<CreateSolutionResponse>(`/sub-projects/${subProjectId}/solutions/`, data);
    return response.data;
  },

  async update(subProjectId: string, solutionId: string, data: UpdateSolutionRequest, version: number): Promise<Solution> {
    const response = await api.put<Solution>(
      `/sub-projects/${subProjectId}/solutions/${solutionId}?version=${version}`,
      data,
    );
    return response.data;
  },

  async delete(subProjectId: string, solutionId: string): Promise<void> {
    await api.delete(`/sub-projects/${subProjectId}/solutions/${solutionId}`);
  },

  async linkKnowledgeBase(subProjectId: string, solutionId: string, kbId: string, context: string): Promise<Solution> {
    const response = await api.post<Solution>(
      `/sub-projects/${subProjectId}/solutions/${solutionId}/knowledge-bases/${kbId}?context=${context}`,
    );
    return response.data;
  },

  async unlinkKnowledgeBase(subProjectId: string, solutionId: string, kbId: string, context: string): Promise<Solution> {
    const response = await api.delete<Solution>(
      `/sub-projects/${subProjectId}/solutions/${solutionId}/knowledge-bases/${kbId}?context=${context}`,
    );
    return response.data;
  },

  async patchFtoCountries(subProjectId: string, solutionId: string, codes: string[]): Promise<void> {
    await api.patch(`/sub-projects/${subProjectId}/solutions/${solutionId}/fto-countries`, { codes });
  },

  async patchFtoInitialPatents(subProjectId: string, solutionId: string, document_ids: string[]): Promise<void> {
    await api.patch(`/sub-projects/${subProjectId}/solutions/${solutionId}/fto-initial-patents`, { document_ids });
  },

  async recomputeFtoFamilies(subProjectId: string, solutionId: string): Promise<void> {
    await api.post(`/sub-projects/${subProjectId}/solutions/${solutionId}/fto-final-patents/recompute`);
  },

  async getFtoRecomputeProgress(
    subProjectId: string,
    solutionId: string,
  ): Promise<{ phase: string; current: number; total: number }> {
    const { data } = await api.get(
      `/sub-projects/${subProjectId}/solutions/${solutionId}/fto-recompute-progress`,
    );
    return data;
  },

  async getFtoPatentClaims(
    subProjectId: string,
    solutionId: string,
    patentId: string,
  ): Promise<{ patent_id: string; claims_text: string; version_id: string | null }> {
    const r = await api.get(
      `/sub-projects/${subProjectId}/solutions/${solutionId}/fto-patent-claims/${patentId}`,
    );
    return r.data;
  },
};
